import React from "react";
import { useTranslation } from "react-i18next";
import { Wind } from "lucide-react";

interface WindDirectionInputProps {
  value?: number | null;
  onChange: (value: number | undefined) => void;
  className?: string;
}

// Define compass directions in 3x3 grid layout
const compassGrid = [
  [
    { label: "NW", degrees: 315 },
    { label: "N", degrees: 0 },
    { label: "NE", degrees: 45 },
  ],
  [
    { label: "W", degrees: 270 },
    null, // Center position - no button
    { label: "E", degrees: 90 },
  ],
  [
    { label: "SW", degrees: 225 },
    { label: "S", degrees: 180 },
    { label: "SE", degrees: 135 },
  ],
];

export const WindDirectionInput: React.FC<WindDirectionInputProps> = ({
  value,
  onChange,
  className = "",
}) => {
  const { t } = useTranslation("pages");
  // Find the active compass direction based on the current value
  const getActiveDirection = () => {
    if (value === undefined || value === null) return null;
    // Flatten the grid to find the matching direction
    for (const row of compassGrid) {
      for (const direction of row) {
        if (direction && direction.degrees === value) {
          return direction;
        }
      }
    }
    return null;
  };

  const handleCompassClick = (degrees: number) => {
    onChange(degrees);
  };

  const handleInputChange = (inputValue: string) => {
    if (inputValue === "") {
      onChange(undefined);
    } else {
      const numValue = Number(inputValue);
      if (!isNaN(numValue) && numValue >= 0 && numValue <= 360) {
        onChange(numValue);
      }
    }
  };

  const formatWindDirection = (degrees?: number) => {
    if (degrees === undefined) return "";
    // Find direction in the grid
    for (const row of compassGrid) {
      for (const direction of row) {
        if (direction && direction.degrees === degrees) {
          return `${direction.label} (${degrees}°)`;
        }
      }
    }
    return `${degrees}°`;
  };

  const activeDirection = getActiveDirection();

  return (
 <div className={className}>
 <label className="block text-sm font-medium text-secondary-700 mb-2">
        {t("sessions.detail.conditions.fields.windDirection")}
      </label>

      {/* 3x3 Grid compass buttons */}
 <div className="space-y-3">
 <div className="grid grid-cols-3 gap-2 w-fit mx-auto">
          {compassGrid.map((row, rowIndex) =>
            row.map((direction, colIndex) => (
 <div key={`${rowIndex}-${colIndex}`} className="w-12 h-10">
                {direction ? (
                  <button
                    type="button"
                    onClick={() => handleCompassClick(direction.degrees)}
 className={`w-full h-full rounded-lg text-sm font-medium transition-colors ${
                      activeDirection?.degrees === direction.degrees
                        ? "bg-primary-500 text-white"
                        : "bg-white text-secondary-700 border border-secondary-300 hover:bg-secondary-100"
                    }`}
                    title={`${direction.label} (${direction.degrees}°)`}
                  >
                    {direction.label}
                  </button>
                ) : (
                  // Center position - show wind icon
 <div className="w-full h-full flex items-center justify-center">
 <Wind className="h-4 w-4 text-secondary-400" />
                  </div>
                )}
              </div>
            )),
          )}
        </div>

        {/* Current direction display */}
        {value !== undefined && value !== null && (
          <div className="flex items-center space-x-2 text-sm text-secondary-600">
            <Wind className="h-4 w-4" />
            <span>{formatWindDirection(value)}</span>
          </div>
        )}
      </div>

      {/* Number input for advanced users */}
 <div className="mt-4">
 <label className="block text-xs text-secondary-500 mb-1">
          {t("sessions.detail.conditions.windDirection.advancedLabel")}
        </label>
 <div className="relative">
          <input
            type="number"
            value={value || ""}
            onChange={(e) => handleInputChange(e.target.value)}
 className="form-input w-full pr-8"
            placeholder="0"
            min="0"
            max="360"
          />
 <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-secondary-400">
            °
          </span>
        </div>
 <div className="mt-1 text-xs text-secondary-500">
          {t("sessions.detail.conditions.windDirection.help")}
        </div>
      </div>
    </div>
  );
};
